import { planDisplayName } from './planDisplay'

/** Monitor and store allowances. null is unlimited. */

export const RECIPE_PLAN_ALLOWANCES = {
  Free: { storeMax: 50, monitorMax: 5 },
  Starter: { storeMax: 200, monitorMax: 50 },
  Growth: { storeMax: null, monitorMax: 100 },
  Pro: { storeMax: null, monitorMax: null },
}

const normalizePlan = plan => {
  const raw = String(plan || 'Free').trim()
  if (!raw) return 'Free'
  const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
  return RECIPE_PLAN_ALLOWANCES[normalized] ? normalized : 'Free'
}

export const getRecipePlanAllowance = plan =>
  RECIPE_PLAN_ALLOWANCES[normalizePlan(plan)]

const capPhrase = (max, verb) =>
  max == null ? `${verb} unlimited recipes` : `${verb} up to ${max} recipes`

export const formatRecipePlanAllowance = plan => {
  const allowance = getRecipePlanAllowance(plan)
  return {
    plan: normalizePlan(plan),
    monitor: capPhrase(allowance.monitorMax, 'Monitor'),
    store: capPhrase(allowance.storeMax, 'Store'),
  }
}

/** Lines for the subscriptions grid. */
export const recipePlanFeatureLines = plan => {
  const normalized = normalizePlan(plan)
  if (normalized === 'Pro') {
    return [
      { text: 'Monitor and store unlimited recipes' },
      { text: 'Access full Menu Costing tools' },
    ]
  }

  const { monitor, store } = formatRecipePlanAllowance(normalized)
  // Included allowances use ticks; locks are only for unavailable features.
  return [{ text: monitor }, { text: store }]
}

export const formatRecipePlanBenefit = plan => {
  const normalized = normalizePlan(plan)
  const allowance = getRecipePlanAllowance(normalized)
  if (normalized === 'Pro') return 'Pro — monitor and store unlimited'
  const store =
    allowance.storeMax == null ? 'unlimited' : String(allowance.storeMax)
  return `${planDisplayName(normalized)} — monitor ${
    allowance.monitorMax
  } · store ${store}`
}

/** At the cap, the next save is stored and not monitored. Over the cap is a downgrade. */
export const isAtRecipeMonitorCap = (allowance = {}) =>
  allowance?.monitoredMax != null &&
  Number(allowance.monitoredCount) >= Number(allowance.monitoredMax) &&
  !(Number(allowance.recipesToArchive) > 0)

export const isOverRecipeMonitorCap = (allowance = {}) =>
  Number(allowance?.recipesToArchive) > 0

export const formatArchiveOverage = ({
  currentPlan = 'Free',
  monitoredCount = 0,
  monitoredMax = null,
  recipesToArchive = 0,
} = {}) => {
  const noun = Number(recipesToArchive) === 1 ? 'recipe' : 'recipes'
  return `Stop monitoring ${recipesToArchive} ${noun} — they stay stored. Your ${planDisplayName(
    currentPlan,
  )} plan monitors ${monitoredMax}, and ${monitoredCount} are already monitored.`
}

export const recipeArchiveOverageLine = (allowance = {}) => {
  if (!isOverRecipeMonitorCap(allowance)) return null
  return (
    allowance.allowanceMessage ||
    formatArchiveOverage({
      currentPlan: allowance.currentPlan || 'Free',
      monitoredCount: allowance.monitoredCount,
      monitoredMax: allowance.monitoredMax,
      recipesToArchive: allowance.recipesToArchive,
    })
  )
}
